import { Fragment, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import TaskItem from "./TaskItem";
import EmptyState from "../../../common/core/EmptyState";
import { sortByName, sortByETA } from "../../../../common/helpers";
import moment from "moment/moment";
import { groupBy } from "@emberly/zenith-client";
import { useTranslation } from "react-i18next";
import { TaskEnumsLists } from "../../../../common/constants";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      width: "100%",
      position: "relative",
    },
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1.5)
    },
    loader: {
      paddingTop: "128px"
    },
    urgencyGroupHeader: {
      textTransform: "uppercase",
      paddingTop: theme.spacing(0.5)
    },
    emptyStateContainer: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      background: "rgba(0,0,0,0.035)"
    }
  })
);

export default function TaskList(props) {
  const { showLoading, tasks, slim, emptyStateTitle, emptyStateDescription, orderTasksBy, emptyStateFilteredDescription, cards, highlightTaskById, onItemClicked, onItemMouseEnter, onItemMouseLeave } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const sortedTasks = useMemo(() => {
    if (!orderTasksBy) return tasks;

    return tasks
      .sort(
        (a, b) =>
          orderTasksBy === "urgency" ? b.urgency - a.urgency :
            orderTasksBy === "new" ? moment(b.execution.history?.created?.created).diff(a.execution.history?.created?.created) :
              orderTasksBy === "old" ? moment(a.execution.history?.created?.created).diff(b.execution.history?.created?.created) :
                orderTasksBy === "state_asc" ? a.state - b.state :
                  orderTasksBy === "state_desc" ? b.state - a.state :
                    orderTasksBy === "driver" ? sortByName(a, b) :
                      orderTasksBy === "eta" ? sortByETA(a, b) : 0
      )
  }, [tasks, orderTasksBy]);

  const groups = useMemo(() => {
    if (orderTasksBy !== "urgency") return null;
    return [...groupBy(sortedTasks, task => task.urgency).entries()];
  }, [sortedTasks, orderTasksBy]);

  return (
    <Stack className={classes.root} spacing={1}>
      {
        showLoading ? (
          <Stack justifyContent="center" alignItems="center" className={classes.loader}>
            <CircularProgress variant="indeterminate" size="48px" />
          </Stack>
        ) : sortedTasks.length === 0 ? (
          <Box className={classes.emptyStateContainer}>
            <EmptyState
              title={emptyStateTitle}
              description={tasks.length === 0 ? emptyStateDescription : emptyStateFilteredDescription}
            />
          </Box>
        ) : orderTasksBy === "urgency" ? (
          groups.map((kv, key) => (
            <Fragment key={key}>
              <Typography color="textDisabled" variant="body2" fontWeight={600} className={classes.urgencyGroupHeader}>{t(`task:enums:estimatedUrgencyAlternate:${TaskEnumsLists.EstimatedUrgency[kv[0]]}`)}</Typography>
              {kv[1].map((task, i) => (
                <TaskItem
                  key={i}
                  slim={slim}
                  task={task}
                  card={cards}
                  highlight={task.id === highlightTaskById}
                  onClick={onItemClicked}
                  onMouseEnter={onItemMouseEnter}
                  onMouseLeave={onItemMouseLeave}
                />
              ))}
            </Fragment>
          ))
        ) : sortedTasks.map((task, i) => (
          <TaskItem
            key={i}
            slim={slim}
            task={task}
            card={cards}
            highlight={task.id === highlightTaskById}
            onClick={onItemClicked}
            onMouseEnter={onItemMouseEnter}
            onMouseLeave={onItemMouseLeave}
          />
        ))}

    </Stack>
  );
}